import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { distinctUntilChanged, filter, fromEvent, Observable, Subscription } from 'rxjs';

import { faCircleExclamation, faCircleInfo, faLock, faSpinner, faUser, faSquareCaretUp } from '@fortawesome/free-solid-svg-icons';

import { LoginService } from '../../core/services/login.service';
import { SessionStorageService } from "../../core/services/session-storage.service";
import { LocalStorageService } from '../../core/services/local-storage.service';
import { environment } from '../../../environments/environment';
import * as Sentry from '@sentry/angular';
// Import SessionTimerService if you need to interact with it directly after login
import { SessionTimerService } from 'src/app/core/services/session-timer.service';

@Component({
    selector: 'wf-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  spinnerIcon = faSpinner ;
  loginErrorIcon = faCircleExclamation ;
  userIcon = faUser ;
  passwordIcon = faLock ;
  capsLockIcon = faSquareCaretUp ;
  capsOn: boolean = false;
  result: string | null = null ;
  loading: boolean = false ;
  returnedLoginError: boolean = false ;
  returnedVersionError: boolean = false ;
  returnedMaintenanceError: boolean = false; // Added for specific maintenance message
  returnedError: boolean = false ;
  returnTitle: string | null = null ;
  accountLocked: boolean = false ;
  warnAccountLocked: boolean = false ;
  failedAttempts: number = 0 ;
  attemptsRemaining: number = 0 ;
  lockoutDuration: number = 0 ;

  lastUsername: string | null = this.localService.getLastUsername();
  rememberMe: boolean = !!this.localService.getLastUsername();
  loginForm = new FormGroup({
    // Keep the { value: ..., disabled: ... } pattern for initial state
    username: new FormControl({ value: this.lastUsername || '', disabled: false }, Validators.required),
    password: new FormControl({ value: '', disabled: false }, Validators.required),
    rememberMe: new FormControl({ value: this.rememberMe, disabled: false }),
  })
  displayForm: boolean = true;
  cleverOauth = `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${encodeURIComponent(environment.CLEVER_REDIRECT_URI)}&client_id=${environment.CLEVER_CLIENT_ID}` ;
  classlinkOauth = `https://launchpad.classlink.com/oauth2/v2/auth?scope=profile,oneroster,full&redirect_uri=${encodeURIComponent(environment.CLASSLINK_REDIRECT_URI)}&client_id=${environment.CLASSLINK_CLIENT_ID}&response_type=code` ;

  private keyPressListener$: Observable<KeyboardEvent> ;
  private keyPressSubscription: Subscription | null = null ;

  constructor(
    private loginService: LoginService,
    private sessionService: SessionStorageService,
    private localService: LocalStorageService,
    private sessionTimerService: SessionTimerService,
  ) {
    this.keyPressListener$ = fromEvent<KeyboardEvent>(window, 'keydown').pipe(
      filter((e: KeyboardEvent) => e.code === 'CapsLock'),
      distinctUntilChanged()
    ) ;
  }

  ngOnInit(): void {
    if (this.lastUsername) {
      this.loginForm.controls['username'].setValue(this.lastUsername);
      this.loginForm.controls['rememberMe'].setValue(this.rememberMe);
    }

    this.keyPressSubscription = this.keyPressListener$.subscribe(() => {
      this.capsOn = !this.capsOn ;
    }) ;

    const logoutMsg = this.sessionService.getLogoutMessage();
    if (logoutMsg != null)
    {
      this.loading = false ;
      this.returnedLoginError = false;
      this.returnedVersionError = false;
      this.returnedMaintenanceError = false;
      this.returnedError = true ;
      this.loginErrorIcon = faCircleInfo ;
      this.returnTitle = logoutMsg.title;
      this.result = logoutMsg.msg;

      this.sessionService.setLogoutMessage('') ;
    }
  }

  ngOnDestroy() {
    this.keyPressSubscription?.unsubscribe() ;
  }

  login() {
    this.loading = true ;
    this.returnedError = false;
    this.returnedLoginError = false;
    this.returnedVersionError = false;
    this.returnedMaintenanceError = false;
    this.accountLocked = false ;
    this.warnAccountLocked = false ;
    this.result = null;
    this.returnTitle = null;
    this.loginForm.disable() ;

    const username = this.loginForm.get('username')?.value as string;
    const password = this.loginForm.get('password')?.value as string;

    this.loginService.loginWithChecks(username, password).subscribe({
      next: (response) => {
        this.loading = false ;

        switch (response.status) {
          case 'maintenance':
            this.returnedMaintenanceError = true ;
            this.returnedError = true ;
            this.returnTitle = 'Maintenance Scheduled';
            this.result = 'The application is temporarily unavailable due to scheduled maintenance. Please try again later.'
            this.loginErrorIcon = faCircleInfo ;
            this.loginForm.enable() ;
            break ;

          case 'update':
            this.returnedVersionError = true ;
            this.returnedError = true ;
            this.returnTitle = 'Update Available';
            this.result = 'A new version of the management application is available. The page will reload shortly...';
            this.loginErrorIcon = faCircleInfo ;
            setTimeout(() => {
              window.location.reload()
            }, 2500);
            break ;

          case 'success':
            const { loginResponse } = response ;
            this.returnedLoginError = false ;
            this.returnedVersionError = false ;
            this.returnedMaintenanceError = false;
            this.returnedError = false ;
            this.result = '' ;
            this.returnTitle = '';
            this.accountLocked = false ;
            this.warnAccountLocked = false ;

            if (this.loginForm.get('rememberMe')?.value) {
              this.localService.setLastUsername(username)
            } else {
              this.localService.removeLastUsername()
            }

            if (loginResponse.isLoggedIn)
            {
              this.displayForm = false;
              this.sessionTimerService.startSleepAndInactivityTimers();
              this.sessionTimerService.checkForMaintenanceAndUpdateState();

              this.loginService.redirectAfterLogin(this.sessionService.userData(), loginResponse) ;
            } else {
              this.handleLoginError({ status: 401 });
            }
            break;
        }
      },
      error: (error) => {
        this.loading = false ;
        this.loginForm.enable() ;
        this.handleLoginError(error);
      }
    });
  }

  private handleLoginError(error: any) {
    let errorCode = error?.status ?? -1 ;
    this.loading = false;
    this.loginForm.enable();
    this.returnedLoginError = true ;
    this.returnedError = true ;
    this.returnTitle = 'Login Failed' ;
    this.loginErrorIcon = faCircleExclamation ;

    console.error("Login Error:", error);

    switch (errorCode) {
        case 423:
            this.accountLocked = true ;
            this.warnAccountLocked = false ;
            if (error.error) {
                this.lockoutDuration = error.error.duration ;
                this.failedAttempts = error.error.attempts ;
                this.result = `Your account is temporarily locked for ${this.lockoutDuration} minutes due to ${this.failedAttempts} failed attempts.`;
            } else {
                this.result = 'Your account is temporarily locked due to too many failed login attempts.';
            }
            break;
        case 401:
            if (error.error?.warning) {
                this.warnAccountLocked = true ;
                this.accountLocked = false ;
                this.attemptsRemaining = error.error.attemptsRemaining ;
                this.result = `Invalid username or password. ${this.attemptsRemaining} attempts remaining before lockout.`;
            } else {
                this.result = 'The username or password you entered is incorrect.' ;
            }
            break;
        case 406:
            this.result = 'Looks like that didn\'t work. Would you like to try logging in with another provider?' ;
            break;
        case 403:
             this.result = "You do not have the correct permissions to access this application.";
             break;
        case 503:
            this.result = 'The login service is temporarily unavailable. Please try again later.';
            break;
        case -1:
        case 500:
        default:
            this.result = 'The application is temporarily unavailable. Please wait a few moments and try again.' ;
            if (errorCode < 400 || errorCode >= 500 || errorCode === -1) {
                 Sentry.captureException(error, {
                   tags: {
                     section: 'wf-mgmt-login',
                     httpStatus: errorCode,
                   }
                 }) ;
            }
            break;
    }
  }
}
